"use client";

import * as React from "react";
import Link from "next/link";
import { ChartCard, PreviewLineChart } from "@/components";
import { HeartbeatIcon } from "@/icons";
import { heartbeatRowsForUnit, useHeartbeat } from "@/hooks/use-heartbeat";
import { formatHexCompactLabel } from "@/utils/format-hex";
import { cn } from "@/utils";

const HEARTBEAT_LINES = [
  { dataKey: "inflow", name: "Stake inflow", stroke: "#03FF82" },
  { dataKey: "outflow", name: "Stake outflow", stroke: "#FF851F" },
  { dataKey: "penalties", name: "Penalties", stroke: "#FF0F6F" },
] as const;

type HeartbeatPreviewProps = {
  compact?: boolean;
  className?: string;
};

export function HeartbeatPreview({ compact, className }: HeartbeatPreviewProps) {
  const { data, isLoading } = useHeartbeat();
  const summary = data?.summary;
  const chartRows = heartbeatRowsForUnit(data?.data ?? [], "hex");

  const metrics = [
    {
      label: "Stake inflow",
      value: summary ? formatHexCompactLabel(summary.inflowHex) : "—",
      color: "text-emerald-400",
      dot: "bg-emerald-400",
    },
    {
      label: "Stake outflow",
      value: summary ? formatHexCompactLabel(summary.outflowHex) : "—",
      color: "text-orange-400",
      dot: "bg-orange-400",
    },
    {
      label: "Penalties",
      value: summary ? formatHexCompactLabel(summary.penaltiesHex) : "—",
      color: "text-fuchsia-400",
      dot: "bg-fuchsia-400",
    },
  ];

  return (
    <div className={cn("flex h-full min-h-[14rem] flex-col gap-4", className)}>
      <div
        className={cn(
          "grid gap-2",
          compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-3",
        )}
      >
        {metrics.map(({ label, value, color, dot }) => (
          <div
            key={label}
            className="rounded-xl border border-white/[0.06] bg-surface-300/20 px-3 py-2.5"
          >
            <div className="flex items-center gap-2">
              <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
              <p className="text-2xs font-medium uppercase tracking-wider text-muted">
                {label}
              </p>
            </div>
            <p className={cn("mt-1 font-mono text-sm font-semibold", color)}>
              {isLoading ? (
                <span className="inline-block h-4 w-20 animate-pulse rounded bg-surface-400" />
              ) : (
                value
              )}
            </p>
            {!compact && (
              <p className="mt-0.5 text-2xs text-muted/80">30d · eHEX + pHEX</p>
            )}
          </div>
        ))}
      </div>
      <div className="min-h-0 flex-1">
        <PreviewLineChart data={chartRows} lines={[...HEARTBEAT_LINES]} />
      </div>
    </div>
  );
}

export function Heartbeat() {
  const { isLoading } = useHeartbeat();

  return (
    <Link href="/explore/heartbeat" className="block h-full cursor-pointer">
      <ChartCard
        title="Heartbeat"
        icon={<HeartbeatIcon size={20} color="#FF0F6F" />}
        isLoading={isLoading}
        accent="30 days"
        description="Daily stake flows and penalties across both chains"
      >
        {!isLoading && <HeartbeatPreview compact />}
      </ChartCard>
    </Link>
  );
}
